///
/// EarthBrowser
///
/// Created for Passkey Authentication
/// Copyright (c) 2025 Geodata Labs
///

import type {
	PasskeyRegistrationOptions,
	PasskeyRegistrationCredential,
	PasskeyAuthenticationOptions,
	PasskeyAuthenticationCredential,
} from "@shared-types/passkey"

/**
 * Check if WebAuthn is supported in the browser
 */
export function isWebAuthnSupported(): boolean {
	return (
		typeof window !== "undefined" &&
		window.PublicKeyCredential !== undefined &&
		typeof window.PublicKeyCredential === "function"
	)
}

/**
 * Check if platform authenticator (e.g., Face ID, Touch ID) is available
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
	if (!isWebAuthnSupported()) {
		return false
	}

	try {
		return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
	} catch {
		return false
	}
}

/**
 * Check if conditional UI (autofill) is supported
 */
export async function isConditionalMediationAvailable(): Promise<boolean> {
	if (!isWebAuthnSupported()) {
		return false
	}

	try {
		return await PublicKeyCredential.isConditionalMediationAvailable()
	} catch {
		return false
	}
}

/**
 * Convert base64url string to ArrayBuffer
 */
function base64URLToBuffer(base64url: string): ArrayBuffer {
	const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/")
	const padLength = (4 - (base64.length % 4)) % 4
	const padded = base64 + "=".repeat(padLength)
	const binary = atob(padded)
	const bytes = new Uint8Array(binary.length)
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i)
	}
	return bytes.buffer
}

/**
 * Convert ArrayBuffer to base64url string
 */
function bufferToBase64URL(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer)
	let binary = ""
	for (const byte of bytes) {
		binary += String.fromCharCode(byte)
	}
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

/**
 * Register a new passkey
 */
export async function registerPasskey(
	options: PasskeyRegistrationOptions
): Promise<PasskeyRegistrationCredential> {
	if (!isWebAuthnSupported()) {
		throw new Error("WebAuthn is not supported in this browser")
	}

	try {
		// Convert options to the format expected by navigator.credentials.create()
		const publicKeyOptions: PublicKeyCredentialCreationOptions = {
			challenge: base64URLToBuffer(options.challenge),
			rp: options.rp,
			user: {
				id: base64URLToBuffer(options.user.id),
				name: options.user.name,
				displayName: options.user.displayName,
			},
			pubKeyCredParams: options.pubKeyCredParams.map((param) => ({
				type: "public-key",
				alg: param.alg,
			})),
			timeout: options.timeout,
			authenticatorSelection: options.authenticatorSelection,
			attestation: options.attestation,
			excludeCredentials: options.excludeCredentials?.map((cred) => ({
				id: base64URLToBuffer(cred.id),
				type: "public-key",
				transports: cred.transports as AuthenticatorTransport[],
			})),
		}

		// Create the credential
		const credential = (await navigator.credentials.create({
			publicKey: publicKeyOptions,
		})) as PublicKeyCredential | null

		if (!credential) {
			throw new Error("Failed to create credential")
		}

		const response = credential.response as AuthenticatorAttestationResponse

		// Convert to serializable format
		return {
			id: credential.id,
			rawId: bufferToBase64URL(credential.rawId),
			response: {
				clientDataJSON: bufferToBase64URL(response.clientDataJSON),
				attestationObject: bufferToBase64URL(response.attestationObject),
				transports: response.getTransports ? response.getTransports() : undefined,
			},
			type: credential.type,
			authenticatorAttachment: credential.authenticatorAttachment || undefined,
			clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
		}
	} catch (error) {
		console.error("Passkey registration error:", error)
		if (error instanceof Error) {
			// Map common error names to user-friendly messages
			if (error.name === "NotAllowedError") {
				throw new Error("Registration was cancelled or not allowed")
			} else if (error.name === "InvalidStateError") {
				throw new Error("This passkey is already registered")
			} else if (error.name === "NotSupportedError") {
				throw new Error("Passkeys are not supported on this device")
			}
		}
		throw error
	}
}

/**
 * Authenticate with a passkey
 */
export async function authenticateWithPasskey(
	options: PasskeyAuthenticationOptions,
	conditional?: boolean
): Promise<PasskeyAuthenticationCredential> {
	if (!isWebAuthnSupported()) {
		throw new Error("WebAuthn is not supported in this browser")
	}

	try {
		// Convert options to the format expected by navigator.credentials.get()
		const publicKeyOptions: PublicKeyCredentialRequestOptions = {
			challenge: base64URLToBuffer(options.challenge),
			rpId: options.rpId,
			timeout: options.timeout,
			userVerification: options.userVerification,
			allowCredentials: options.allowCredentials?.map((cred) => ({
				id: base64URLToBuffer(cred.id),
				type: "public-key",
				transports: cred.transports as AuthenticatorTransport[],
			})),
		}

		// Get the credential
		const credential = (await navigator.credentials.get({
			publicKey: publicKeyOptions,
			mediation: conditional ? "conditional" : undefined,
		})) as PublicKeyCredential | null

		if (!credential) {
			throw new Error("Failed to get credential")
		}

		const response = credential.response as AuthenticatorAssertionResponse

		// Convert to serializable format
		return {
			id: credential.id,
			rawId: bufferToBase64URL(credential.rawId),
			response: {
				clientDataJSON: bufferToBase64URL(response.clientDataJSON),
				authenticatorData: bufferToBase64URL(response.authenticatorData),
				signature: bufferToBase64URL(response.signature),
				userHandle: response.userHandle
					? bufferToBase64URL(response.userHandle)
					: undefined,
			},
			type: credential.type,
			authenticatorAttachment: credential.authenticatorAttachment || undefined,
			clientExtensionResults: credential.getClientExtensionResults() as Record<string, unknown>,
		}
	} catch (error) {
		console.error("Passkey authentication error:", error)
		if (error instanceof Error) {
			// Map common error names to user-friendly messages
			if (error.name === "NotAllowedError") {
				throw new Error("Authentication was cancelled or not allowed")
			} else if (error.name === "NotSupportedError") {
				throw new Error("Passkeys are not supported on this device")
			} else if (error.name === "SecurityError") {
				throw new Error("Security error occurred during authentication")
			}
		}
		throw error
	}
}

/**
 * Start conditional authentication (autofill)
 * This allows passkey authentication to be triggered from an email/username input field
 */
export async function startConditionalAuthentication(
	getOptions: () => Promise<PasskeyAuthenticationOptions>,
	onAuthenticate: (credential: PasskeyAuthenticationCredential) => Promise<void>
): Promise<void> {
	if (!isWebAuthnSupported()) {
		return
	}

	const isConditionalAvailable = await isConditionalMediationAvailable()
	if (!isConditionalAvailable) {
		return
	}

	try {
		const options = await getOptions()
		const credential = await authenticateWithPasskey(options, true)
		await onAuthenticate(credential)
	} catch (error) {
		// Silently fail for conditional authentication
		// The user can still use the regular authentication flow
		console.debug("Conditional authentication not completed:", error)
	}
}
